/**
 * Gemini Data Sanitizer
 * Prepares portfolio data for Gemini AI analysis by removing PII and creating an anonymized summary
 */

import {
  compareStrategies,
  categorizeDebtByInterest,
  calculateTotalMonthlyPayment,
  getDebtPriorities
} from './debtOptimization';

import {
  categorizeGoalsByTimeHorizon,
  detectGoalConflicts,
  generateBucketStrategy,
  recommendAllocationByGoal
} from './goalBasedAllocation';

import {
  calculateFinancialPriorityWaterfall
} from './financialPriorityWaterfall';

/**
 * Sanitize portfolio data for Gemini API
 * Removes all PII (names, emails, account numbers, precise monetary values)
 * Converts to percentages and categories only
 *
 * @param {Array} investments - User's investment holdings
 * @param {Array} accounts - User's account containers
 * @param {Array} cryptocurrencies - User's crypto holdings (legacy)
 * @param {Object} userProfile - User profile data (country, risk tolerance, etc.)
 * @param {Object} retirementGoals - Retirement planning data
 * @param {Array} liabilities - User's liabilities/debts
 * @param {Array} financialGoals - User's financial goals
 * @param {Number} cashReserves - User's cash reserves
 * @returns {Object} Sanitized portfolio summary safe for external API
 */
export const sanitizePortfolioForGemini = (
  investments = [],
  accounts = [],
  cryptocurrencies = [],
  userProfile = {},
  retirementGoals = {},
  liabilities = [],
  financialGoals = [],
  cashReserves = 0
) => {
  // Calculate total portfolio value
  const totalInvestmentValue = investments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalCryptoValue = cryptocurrencies.reduce((sum, crypto) => sum + (crypto.amount * (crypto.price || 0)), 0);
  const totalValue = totalInvestmentValue + totalCryptoValue;

  if (totalValue === 0) {
    return {
      portfolio_summary: {
        error: 'No portfolio data available',
        total_accounts: 0,
        asset_allocation: [],
        message: 'Please add investments to your portfolio to generate an analysis.'
      }
    };
  }

  // 1. ASSET ALLOCATION - Aggregate by asset class
  const assetMap = {};

  investments.forEach(inv => {
    const category = inv.category || 'Other';
    if (!assetMap[category]) {
      assetMap[category] = 0;
    }
    assetMap[category] += inv.amount || 0;
  });

  // Add crypto as a separate category
  if (totalCryptoValue > 0) {
    assetMap['Cryptocurrency'] = totalCryptoValue;
  }

  const asset_allocation = Object.entries(assetMap).map(([assetClass, value]) => ({
    class: assetClass,
    percentage: parseFloat(((value / totalValue) * 100).toFixed(2))
  })).sort((a, b) => b.percentage - a.percentage);

  // 2. GEOGRAPHIC ALLOCATION - Infer from tickers and categories
  const geographic_allocation = inferGeographicAllocation(investments);

  // 3. SECTOR ALLOCATION - Infer from investment categories
  const sector_allocation = inferSectorAllocation(investments, totalValue);

  // 4. TOP HOLDINGS - By percentage (anonymized)
  const top_holdings_by_percentage = investments
    .map(inv => ({
      ticker: inv.ticker || 'N/A',
      name: inv.name ? inv.name.substring(0, 30) : 'Unknown', // Truncate names
      percentage: parseFloat(((inv.amount / totalValue) * 100).toFixed(2)),
      category: inv.category
    }))
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 10); // Top 10 holdings only

  // 5. ACCOUNT TYPES - Anonymized
  const account_types = [...new Set(accounts.map(acc => anonymizeAccountType(acc.type)))];

  // 6. USER CONTEXT - Anonymized risk profile and time horizon
  const user_context = {
    risk_profile: determineRiskProfile(investments, totalValue),
    investment_horizon_years: retirementGoals.years || 20,
    country: userProfile.country || 'US'
  };

  // 7. PORTFOLIO METRICS - Calculated metrics (no PII)
  const portfolio_metrics = calculatePortfolioMetrics(investments, cryptocurrencies, totalValue);

  // Get currency symbol from country (default to USD)
  const country = userProfile.country || 'US';

  // 8. NET WORTH SUMMARY - Anonymized financial health data
  const net_worth_summary = sanitizeNetWorth(
    totalInvestmentValue,
    totalCryptoValue,
    parseFloat(cashReserves) || 0,
    liabilities,
    country
  );

  // 9. FINANCIAL GOALS - Sanitized goal data
  const sanitized_goals = sanitizeFinancialGoals(financialGoals, country);

  // 10. DEBT PAYOFF STRATEGY - If liabilities exist
  const debt_payoff_strategy = sanitizeDebtPayoffStrategy(liabilities, country);

  // 11. GOAL-BASED ALLOCATION - If goals exist
  const goal_based_allocation = sanitizeGoalBasedAllocation(financialGoals, totalValue, country);

  // 12. EMERGENCY FUND ANALYSIS
  const monthlyDebtPayments = calculateTotalMonthlyPayment(liabilities);

  // Extract actual monthly expenses from Emergency Fund goal if it exists
  const emergencyFundGoal = financialGoals.find(g => g.type === 'Emergency Fund');
  const actualMonthlyExpenses = emergencyFundGoal?.monthlyExpenses || null;

  // Debug logging
  console.log('[Emergency Fund Debug] Emergency Fund Goal:', emergencyFundGoal);
  console.log('[Emergency Fund Debug] Monthly Expenses from goal:', actualMonthlyExpenses);
  console.log('[Emergency Fund Debug] Cash Reserves:', cashReserves);

  const emergency_fund_analysis = analyzeEmergencyFund(
    parseFloat(cashReserves) || 0,
    monthlyDebtPayments,
    country,
    actualMonthlyExpenses // Pass actual expenses from goal
  );

  // 13. FINANCIAL PRIORITY WATERFALL
  const financial_priority_waterfall = sanitizeFinancialPriorityWaterfall(
    liabilities,
    financialGoals,
    parseFloat(cashReserves) || 0,
    country
  );

  // Count unique accounts from investments (more accurate than accounts array)
  const uniqueAccountIds = new Set();
  investments.forEach(inv => {
    if (inv.accountId) {
      uniqueAccountIds.add(inv.accountId);
    }
  });
  const totalAccounts = uniqueAccountIds.size > 0 ? uniqueAccountIds.size : (accounts.length > 0 ? accounts.length : 1);

  return {
    portfolio_summary: {
      total_accounts: totalAccounts,
      total_holdings: investments.length,
      asset_allocation,
      geographic_allocation,
      sector_allocation,
      top_holdings_by_percentage,
      account_types,
      user_context,
      portfolio_metrics
    },
    net_worth_summary,
    financial_goals: sanitized_goals,
    debt_payoff_strategy,
    goal_based_allocation,
    emergency_fund_analysis,
    financial_priority_waterfall
  };
};

/**
 * Infer geographic allocation from investment data
 */
function inferGeographicAllocation(investments) {
  const geoMap = {
    'North America': 0,
    'Europe': 0,
    'Asia': 0,
    'Emerging Markets': 0,
    'Global': 0
  };

  const totalValue = investments.reduce((sum, inv) => sum + (inv.amount || 0), 0);

  if (totalValue === 0) return [];

  investments.forEach(inv => {
    const ticker = (inv.ticker || '').toLowerCase();
    const name = (inv.name || '').toLowerCase();

    // Simple heuristic based on ticker patterns
    if (ticker.includes('vti') || ticker.includes('spy') || ticker.includes('qqq') ||
        name.includes('us') || name.includes('united states') || name.includes('s&p')) {
      geoMap['North America'] += inv.amount;
    } else if (ticker.includes('vxus') || ticker.includes('efa') || name.includes('international')) {
      geoMap['Global'] += inv.amount;
    } else if (ticker.includes('vwo') || ticker.includes('eem') || name.includes('emerging')) {
      geoMap['Emerging Markets'] += inv.amount;
    } else if (name.includes('europe') || name.includes('euro')) {
      geoMap['Europe'] += inv.amount;
    } else if (name.includes('asia') || name.includes('china') || name.includes('japan')) {
      geoMap['Asia'] += inv.amount;
    } else {
      // Default to North America if unknown
      geoMap['North America'] += inv.amount;
    }
  });

  return Object.entries(geoMap)
    .filter(([_, value]) => value > 0)
    .map(([region, value]) => ({
      region,
      percentage: parseFloat(((value / totalValue) * 100).toFixed(2))
    }))
    .sort((a, b) => b.percentage - a.percentage);
}

/**
 * Infer sector allocation from investment categories
 */
function inferSectorAllocation(investments, totalValue) {
  const sectorMap = {
    'Technology': 0,
    'Healthcare': 0,
    'Financials': 0,
    'Consumer Discretionary': 0,
    'Consumer Staples': 0,
    'Industrials': 0,
    'Energy': 0,
    'Real Estate': 0,
    'Materials': 0,
    'Utilities': 0,
    'Communication Services': 0,
    'Other': 0
  };

  if (totalValue === 0) return [];

  investments.forEach(inv => {
    const name = (inv.name || '').toLowerCase();
    const ticker = (inv.ticker || '').toLowerCase();
    const category = inv.category || 'Other';

    // Map based on investment name/ticker
    // Technology sector
    if (category === 'Real Estate' || name.includes('reit') || name.includes('real estate')) {
      sectorMap['Real Estate'] += inv.amount;
    } else if (name.includes('tech') || ticker.includes('aapl') || name.includes('apple') ||
               ticker.includes('msft') || name.includes('microsoft') ||
               ticker.includes('googl') || ticker.includes('goog') || name.includes('google') ||
               ticker.includes('amzn') || name.includes('amazon') ||
               ticker.includes('meta') || name.includes('facebook') ||
               ticker.includes('nvda') || name.includes('nvidia') ||
               ticker.includes('tsla') || name.includes('tesla')) {
      sectorMap['Technology'] += inv.amount;
    } else if (name.includes('health') || name.includes('pharma') || name.includes('medical') ||
               ticker.includes('jnj') || ticker.includes('pfr') || ticker.includes('unh')) {
      sectorMap['Healthcare'] += inv.amount;
    } else if (name.includes('bank') || name.includes('financial') || name.includes('insurance') ||
               ticker.includes('jpm') || ticker.includes('bac') || ticker.includes('gs')) {
      sectorMap['Financials'] += inv.amount;
    } else if (name.includes('energy') || name.includes('oil') || name.includes('gas') ||
               ticker.includes('xom') || ticker.includes('cvx')) {
      sectorMap['Energy'] += inv.amount;
    } else if (category === 'Stocks' && (name.includes('index') || name.includes('fund') ||
               name.includes('etf') || name.includes('s&p') || name.includes('total'))) {
      // Only distribute across sectors for broad index funds/ETFs
      // This is a rough estimate for diversified funds
      sectorMap['Technology'] += inv.amount * 0.25;
      sectorMap['Healthcare'] += inv.amount * 0.15;
      sectorMap['Financials'] += inv.amount * 0.15;
      sectorMap['Consumer Discretionary'] += inv.amount * 0.12;
      sectorMap['Industrials'] += inv.amount * 0.10;
      sectorMap['Other'] += inv.amount * 0.23;
    } else {
      // Individual stocks without sector identification go to "Other"
      sectorMap['Other'] += inv.amount;
    }
  });

  return Object.entries(sectorMap)
    .filter(([_, value]) => value > 0)
    .map(([sector, value]) => ({
      sector,
      percentage: parseFloat(((value / totalValue) * 100).toFixed(2))
    }))
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 10); // Top 10 sectors
}

/**
 * Anonymize account type to remove specific PII
 */
function anonymizeAccountType(accountType) {
  const typeMapping = {
    '401k': 'Tax-Advantaged Retirement (401k)',
    'IRA': 'Tax-Advantaged Retirement (IRA)',
    'Roth IRA': 'Tax-Advantaged Retirement (Roth IRA)',
    'SEP IRA': 'Tax-Advantaged Retirement (SEP IRA)',
    'HSA': 'Health Savings Account',
    '529': 'Education Savings (529)',
    'Brokerage': 'Taxable Brokerage',
    'ISA': 'Tax-Advantaged (ISA)',
    'LISA': 'Tax-Advantaged (LISA)',
    'SIPP': 'Tax-Advantaged Retirement (SIPP)',
    'Pension': 'Workplace Pension',
    'GIA': 'General Investment Account'
  };

  return typeMapping[accountType] || 'Investment Account';
}

/**
 * Determine risk profile based on asset allocation
 */
function determineRiskProfile(investments, totalValue) {
  if (totalValue === 0) return 'Unknown';

  // Calculate equity percentage
  const equityValue = investments
    .filter(inv => ['Stocks', 'Cryptocurrency'].includes(inv.category))
    .reduce((sum, inv) => sum + inv.amount, 0);

  const equityPercentage = (equityValue / totalValue) * 100;

  if (equityPercentage >= 80) return 'Aggressive Growth';
  if (equityPercentage >= 60) return 'Growth';
  if (equityPercentage >= 40) return 'Balanced';
  if (equityPercentage >= 20) return 'Conservative';
  return 'Very Conservative';
}

/**
 * Calculate portfolio metrics for context
 */
function calculatePortfolioMetrics(investments, cryptocurrencies, totalValue) {
  // Calculate diversification score (Herfindahl-Hirschman Index)
  const assetMap = {};

  investments.forEach(inv => {
    const category = inv.category || 'Other';
    assetMap[category] = (assetMap[category] || 0) + inv.amount;
  });

  const categoryCount = Object.keys(assetMap).length;

  // Calculate HHI
  let hhi = 0;
  Object.values(assetMap).forEach(value => {
    const share = value / totalValue;
    hhi += share * share;
  });

  const diversificationScore = Math.round((1 - hhi) * 100);

  // Calculate expected return and volatility
  const volatilityMap = {
    'Stocks': 18,
    'Bonds': 5,
    'Real Estate': 12,
    'Commodities': 20,
    'Cash': 1,
    'Cryptocurrency': 60
  };

  let weightedVolatility = 0;
  let weightedReturn = 0;

  investments.forEach(inv => {
    const weight = inv.amount / totalValue;
    const vol = volatilityMap[inv.category] || 15;
    const ret = inv.return || 5;

    weightedVolatility += weight * vol;
    weightedReturn += weight * ret;
  });

  return {
    diversification_score: diversificationScore,
    asset_class_count: categoryCount,
    estimated_volatility: parseFloat(weightedVolatility.toFixed(2)),
    estimated_return: parseFloat(weightedReturn.toFixed(2)),
    total_holdings: investments.length
  };
}

/**
 * Sanitize net worth data - convert exact amounts to ranges and percentages
 */
function sanitizeNetWorth(totalInvestments, totalCrypto, cashReserves, liabilities = [], country = 'US') {
  const totalLiabilities = liabilities.reduce((sum, liability) =>
    sum + (parseFloat(liability.currentBalance) || 0), 0
  );

  const totalAssets = totalInvestments + totalCrypto + cashReserves;
  const netWorth = totalAssets - totalLiabilities;

  // Convert to anonymized range
  const netWorthRange = getAmountRange(netWorth, country);
  const totalAssetsRange = getAmountRange(totalAssets, country);
  const totalLiabilitiesRange = getAmountRange(totalLiabilities, country);

  // Calculate percentages for asset breakdown
  const assetBreakdown = totalAssets > 0 ? {
    investments_percentage: parseFloat(((totalInvestments / totalAssets) * 100).toFixed(2)),
    crypto_percentage: parseFloat(((totalCrypto / totalAssets) * 100).toFixed(2)),
    cash_percentage: parseFloat(((cashReserves / totalAssets) * 100).toFixed(2))
  } : {
    investments_percentage: 0,
    crypto_percentage: 0,
    cash_percentage: 0
  };

  // Sanitize liability breakdown by type (remove specific names)
  const liabilityBreakdown = sanitizeLiabilities(liabilities, totalLiabilities);

  // Calculate debt-to-asset ratio
  const debtToAssetRatio = totalAssets > 0
    ? parseFloat(((totalLiabilities / totalAssets) * 100).toFixed(2))
    : 0;

  // Calculate monthly debt payments
  const monthlyDebtPayments = liabilities.reduce((sum, liability) =>
    sum + (parseFloat(liability.monthlyPayment) || 0), 0
  );

  // Financial health indicator
  const hasPositiveNetWorth = netWorth > 0;
  const hasManageableDebt = debtToAssetRatio < 50;
  const hasEmergencyCash = cashReserves >= (monthlyDebtPayments * 3);

  return {
    net_worth_range: netWorthRange,
    total_assets_range: totalAssetsRange,
    total_liabilities_range: totalLiabilitiesRange,
    is_positive: hasPositiveNetWorth,
    debt_to_asset_ratio: debtToAssetRatio,
    asset_breakdown: assetBreakdown,
    liability_breakdown: liabilityBreakdown,
    monthly_debt_payments_range: getAmountRange(monthlyDebtPayments, country),
    financial_health_indicators: {
      positive_net_worth: hasPositiveNetWorth,
      manageable_debt: hasManageableDebt,
      emergency_fund_adequate: hasEmergencyCash,
      liability_count: liabilities.length
    }
  };
}

/**
 * Convert exact amounts to anonymized ranges
 */
function getAmountRange(amount, country = 'US') {
  const absAmount = Math.abs(amount);
  const currency = country === 'UK' ? '£' : '$';

  if (absAmount === 0) return `${currency}0`;
  if (absAmount < 10000) return `${currency}0-${currency}10k`;
  if (absAmount < 25000) return `${currency}10k-${currency}25k`;
  if (absAmount < 50000) return `${currency}25k-${currency}50k`;
  if (absAmount < 100000) return `${currency}50k-${currency}100k`;
  if (absAmount < 250000) return `${currency}100k-${currency}250k`;
  if (absAmount < 500000) return `${currency}250k-${currency}500k`;
  if (absAmount < 1000000) return `${currency}500k-${currency}1M`;
  if (absAmount < 2000000) return `${currency}1M-${currency}2M`;
  if (absAmount < 5000000) return `${currency}2M-${currency}5M`;
  return `${currency}5M+`;
}

/**
 * Get more granular range for monthly expenses (smaller amounts need finer granularity)
 */
function getMonthlyExpenseRange(amount, country = 'US') {
  const absAmount = Math.abs(amount);
  const currency = country === 'UK' ? '£' : '$';

  if (absAmount === 0) return `${currency}0`;
  if (absAmount < 1000) return `${currency}0-${currency}1k`;
  if (absAmount < 2000) return `${currency}1k-${currency}2k`;
  if (absAmount < 3000) return `${currency}2k-${currency}3k`;
  if (absAmount < 4000) return `${currency}3k-${currency}4k`;
  if (absAmount < 5000) return `${currency}4k-${currency}5k`;
  if (absAmount < 7500) return `${currency}5k-${currency}7.5k`;
  if (absAmount < 10000) return `${currency}7.5k-${currency}10k`;
  if (absAmount < 15000) return `${currency}10k-${currency}15k`;
  return `${currency}15k+`;
}

/**
 * Sanitize liabilities - remove specific names, keep types and rates
 */
function sanitizeLiabilities(liabilities, totalLiabilities) {
  if (totalLiabilities === 0 || liabilities.length === 0) {
    return [];
  }

  // Group by type
  const typeMap = {};

  liabilities.forEach(liability => {
    const type = liability.type || 'Other';
    if (!typeMap[type]) {
      typeMap[type] = {
        total: 0,
        count: 0,
        avgInterestRate: 0,
        interestRateCount: 0
      };
    }

    typeMap[type].total += parseFloat(liability.currentBalance) || 0;
    typeMap[type].count += 1;

    const rate = parseFloat(liability.interestRate) || 0;
    if (rate > 0) {
      typeMap[type].avgInterestRate += rate;
      typeMap[type].interestRateCount += 1;
    }
  });

  // Convert to anonymized array
  return Object.entries(typeMap).map(([type, data]) => ({
    type,
    percentage: parseFloat(((data.total / totalLiabilities) * 100).toFixed(2)),
    count: data.count,
    average_interest_rate: data.interestRateCount > 0
      ? parseFloat((data.avgInterestRate / data.interestRateCount).toFixed(2))
      : 0
  })).sort((a, b) => b.percentage - a.percentage);
}

/**
 * Sanitize financial goals - remove custom names, keep types and metrics
 */
function sanitizeFinancialGoals(goals = [], country = 'US') {
  if (!goals || goals.length === 0) {
    return [];
  }

  const currency = country === 'UK' ? '£' : '$';

  return goals.map(goal => {
    const targetAmount = parseFloat(goal.targetAmount) || 0;
    const currentAmount = parseFloat(goal.currentAmount) || 0;
    const monthlyContribution = parseFloat(goal.monthlyContribution) || 0;

    // Calculate progress percentage
    const progressPercentage = targetAmount > 0
      ? parseFloat(((currentAmount / targetAmount) * 100).toFixed(2))
      : 0;

    // Calculate months remaining
    const targetDate = goal.targetDate ? new Date(goal.targetDate) : null;
    const now = new Date();
    const monthsRemaining = targetDate && targetDate > now
      ? Math.round((targetDate - now) / (1000 * 60 * 60 * 24 * 30))
      : 0;

    // Calculate required monthly savings
    const remainingAmount = Math.max(0, targetAmount - currentAmount);
    const requiredMonthlySavings = monthsRemaining > 0
      ? parseFloat((remainingAmount / monthsRemaining).toFixed(2))
      : 0;

    // Determine status
    let status = 'On Track';
    if (progressPercentage >= 100) {
      status = 'Completed';
    } else if (monthsRemaining <= 0 && progressPercentage < 100) {
      status = 'Overdue';
    } else if (monthsRemaining <= 3 && progressPercentage < 75) {
      status = 'Urgent';
    } else if (requiredMonthlySavings > monthlyContribution * 1.5 && monthlyContribution > 0) {
      status = 'At Risk';
    }

    // Calculate gap between required and actual contributions
    const savingsGap = monthlyContribution > 0
      ? requiredMonthlySavings - monthlyContribution
      : requiredMonthlySavings;

    return {
      type: goal.type || 'Other',
      target_amount_range: getAmountRange(targetAmount, country),
      progress_percentage: progressPercentage,
      months_remaining: monthsRemaining,
      status,
      monthly_contribution_range: getAmountRange(monthlyContribution, country),
      required_monthly_savings_range: getAmountRange(requiredMonthlySavings, country),
      savings_gap_range: getAmountRange(Math.abs(savingsGap), country),
      is_underfunded: savingsGap > 0,
      expected_return_rate: parseFloat(goal.expectedReturn) || 5
    };
  }).filter(goal => goal.target_amount_range !== `${currency}0`); // Filter out $0 goals
}

/**
 * Sanitize debt payoff strategy analysis
 */
function sanitizeDebtPayoffStrategy(liabilities = [], country = 'US') {
  if (!liabilities || liabilities.length === 0) {
    return null;
  }

  // Calculate strategy comparison
  const comparison = compareStrategies(liabilities, 0);

  if (!comparison) {
    return null;
  }

  // Categorize debt by interest rate
  const categorized = categorizeDebtByInterest(liabilities);

  // Get total monthly payments
  const totalMonthlyPayment = calculateTotalMonthlyPayment(liabilities);

  // Get prioritized list
  const priorities = getDebtPriorities(liabilities);

  return {
    total_liabilities: liabilities.length,
    total_monthly_payment_range: getAmountRange(totalMonthlyPayment, country),
    debt_categories: {
      critical_high_interest: categorized.critical.length,
      high_interest: categorized.high.length,
      medium_interest: categorized.medium.length,
      low_interest: categorized.low.length
    },
    avalanche_strategy: {
      estimated_payoff_months: comparison.avalanche.totalMonths,
      estimated_total_interest_range: getAmountRange(comparison.avalanche.totalInterestPaid, country),
      priority_order: comparison.avalanche.payoffOrder.map(d => ({
        type: d.type,
        interest_rate: `${d.interestRate.toFixed(1)}%`,
        balance_range: getAmountRange(d.balance, country)
      }))
    },
    snowball_strategy: {
      estimated_payoff_months: comparison.snowball.totalMonths,
      estimated_total_interest_range: getAmountRange(comparison.snowball.totalInterestPaid, country),
      first_payoff_months: comparison.snowball.payoffOrder[0]?.monthsToPayoff || 0
    },
    comparison: {
      interest_savings_range: getAmountRange(Math.abs(comparison.interestSavings), country),
      time_difference_months: Math.abs(comparison.timeDifference),
      recommended_strategy: comparison.recommendation
    },
    urgency_flags: {
      has_critical_debt: categorized.critical.length > 0,
      has_high_interest_debt: categorized.high.length > 0,
      highest_interest_rate: priorities.length > 0 ? `${priorities[0].interestRate.toFixed(1)}%` : '0%'
    }
  };
}

/**
 * Sanitize goal-based allocation analysis
 */
function sanitizeGoalBasedAllocation(goals = [], totalPortfolioValue = 0, country = 'US') {
  if (!goals || goals.length === 0) {
    return null;
  }

  // Categorize by time horizon
  const categorized = categorizeGoalsByTimeHorizon(goals);

  // Detect conflicts
  const conflicts = detectGoalConflicts(goals);

  // Generate bucket strategy
  const buckets = generateBucketStrategy(goals, totalPortfolioValue);

  return {
    goals_by_time_horizon: {
      short_term: {
        count: categorized.short.length,
        goals: categorized.short.map(g => ({
          type: g.type,
          months_remaining: Math.max(0, Math.ceil((new Date(g.targetDate) - new Date()) / (1000 * 60 * 60 * 24 * 30))),
          recommended_allocation: recommendAllocationByGoal(g)
        }))
      },
      medium_term: {
        count: categorized.medium.length,
        goals: categorized.medium.map(g => ({
          type: g.type,
          months_remaining: Math.max(0, Math.ceil((new Date(g.targetDate) - new Date()) / (1000 * 60 * 60 * 24 * 30))),
          recommended_allocation: recommendAllocationByGoal(g)
        }))
      },
      long_term: {
        count: categorized.long.length,
        goals: categorized.long.map(g => ({
          type: g.type,
          months_remaining: Math.max(0, Math.ceil((new Date(g.targetDate) - new Date()) / (1000 * 60 * 60 * 24 * 30))),
          recommended_allocation: recommendAllocationByGoal(g)
        }))
      }
    },
    bucket_strategy: buckets ? {
      short_term_bucket: {
        goal_count: buckets.bucket1_shortTerm.goals.length,
        target_amount_range: getAmountRange(buckets.bucket1_shortTerm.targetAmount, country),
        recommended_allocation: buckets.bucket1_shortTerm.recommendedAllocation
      },
      medium_term_bucket: {
        goal_count: buckets.bucket2_mediumTerm.goals.length,
        target_amount_range: getAmountRange(buckets.bucket2_mediumTerm.targetAmount, country),
        recommended_allocation: buckets.bucket2_mediumTerm.recommendedAllocation
      },
      long_term_bucket: {
        goal_count: buckets.bucket3_longTerm.goals.length,
        target_amount_range: getAmountRange(buckets.bucket3_longTerm.targetAmount, country),
        recommended_allocation: buckets.bucket3_longTerm.recommendedAllocation
      }
    } : null,
    goal_conflicts: conflicts.map(c => ({
      type: c.type,
      issue: c.issue,
      recommendation: c.recommendation
    }))
  };
}

/**
 * Analyze emergency fund adequacy
 * @param {number} cashReserves - Current cash reserves
 * @param {number} monthlyDebtPayments - Monthly debt payments for estimation
 * @param {string} country - User's country (UK or US)
 * @param {number} actualMonthlyExpenses - Actual monthly expenses from Emergency Fund goal (optional)
 */
function analyzeEmergencyFund(cashReserves = 0, monthlyDebtPayments = 0, country = 'US', actualMonthlyExpenses = null) {
  // Use actual monthly expenses if provided (from Emergency Fund goal),
  // otherwise estimate from debt payments (rough proxy)
  let monthlyExpenses;
  let isEstimated = false;

  if (actualMonthlyExpenses && actualMonthlyExpenses > 0) {
    // User provided actual monthly expenses in Emergency Fund goal - use it!
    monthlyExpenses = actualMonthlyExpenses;
    isEstimated = false;
    console.log('[Emergency Fund Debug] Using ACTUAL monthly expenses:', monthlyExpenses);
  } else {
    // Estimate from debt payments: typical rule is monthly expenses = debt payments * 2.5-3
    monthlyExpenses = monthlyDebtPayments > 0 ? monthlyDebtPayments * 2.5 : 3000;
    isEstimated = true;
    console.log('[Emergency Fund Debug] Using ESTIMATED monthly expenses:', monthlyExpenses, '(from debt payments:', monthlyDebtPayments, ')');
  }

  // Calculate coverage
  const monthsCoverage = monthlyExpenses > 0
    ? cashReserves / monthlyExpenses
    : 0;

  console.log('[Emergency Fund Debug] Calculated months coverage:', monthsCoverage);

  // Determine adequacy
  let status = 'adequate';
  let recommendation = '';

  if (monthsCoverage < 3) {
    status = 'insufficient';
    recommendation = 'Build emergency fund to 3-6 months of expenses as top priority';
  } else if (monthsCoverage < 6) {
    status = 'moderate';
    recommendation = 'Consider increasing to 6 months for additional security';
  } else if (monthsCoverage > 12) {
    status = 'overfunded';
    recommendation = 'Excess cash could be deployed to goals for higher returns';
  } else {
    status = 'adequate';
    recommendation = 'Emergency fund is well-sized';
  }

  return {
    cash_reserves_range: getAmountRange(cashReserves, country),
    estimated_months_coverage: parseFloat(monthsCoverage.toFixed(1)),
    monthly_expenses_range: getMonthlyExpenseRange(monthlyExpenses, country), // Use granular range for monthly expenses
    is_estimated: isEstimated, // True if estimated from debt, false if from Emergency Fund goal
    recommended_months: 6,
    status,
    recommendation,
    adequacy_percentage: Math.min(100, (monthsCoverage / 6) * 100).toFixed(0)
  };
}

/**
 * Sanitize financial priority waterfall analysis
 */
function sanitizeFinancialPriorityWaterfall(
  liabilities = [],
  financialGoals = [],
  cashReserves = 0,
  country = 'US'
) {
  // Calculate estimated income from debt payments (rough proxy)
  const monthlyDebtPayments = calculateTotalMonthlyPayment(liabilities);
  const estimatedMonthlyExpenses = monthlyDebtPayments > 0 ? monthlyDebtPayments * 2.5 : 3000;
  const estimatedMonthlyIncome = estimatedMonthlyExpenses > 0 ? estimatedMonthlyExpenses * 1.4 : 4200;

  // Build financial data for waterfall calculation
  const financialData = {
    monthlyIncome: estimatedMonthlyIncome,
    monthlyExpenses: estimatedMonthlyExpenses,
    cashReserves: parseFloat(cashReserves) || 0,
    liabilities,
    financialGoals,
    hasEmployerMatch: false, // Unknown - conservative assumption
    employerMatchPercentage: 0,
    currentRetirementContribution: 0,
    retirementAccountType: null,
    country
  };

  // Calculate the waterfall
  const waterfall = calculateFinancialPriorityWaterfall(financialData);

  if (!waterfall || !waterfall.priorities) {
    return null;
  }

  // Sanitize the priority list - remove exact amounts, keep structure and guidance
  const sanitizedPriorities = waterfall.priorities.map(priority => ({
    step: priority.step,
    priority: priority.priority,
    description: priority.description,
    status: priority.status,
    recommended_monthly_allocation_range: getAmountRange(priority.recommendedMonthlyAllocation || 0, country),
    country_specific_notes: priority.countrySpecificNotes || []
  }));

  return {
    estimated_monthly_income_range: getAmountRange(estimatedMonthlyIncome, country),
    estimated_monthly_expenses_range: getAmountRange(estimatedMonthlyExpenses, country),
    priorities: sanitizedPriorities,
    total_monthly_allocated_range: getAmountRange(waterfall.totalMonthlyAllocated || 0, country),
    surplus_range: getAmountRange(waterfall.monthlySurplus || 0, country),
    has_surplus: (waterfall.monthlySurplus || 0) > 0,
    country_context: country,
    note: 'Income/expenses are estimated from debt obligations. Priorities based on industry best practices.'
  };
}

/**
 * Validate that sanitized data contains no PII
 * This is a safety check before sending to external API
 */
export const validateNoPII = (sanitizedData) => {
  const dataString = JSON.stringify(sanitizedData);

  // Check for common PII patterns
  const piiPatterns = [
    /@/,  // Email addresses
    /\d{3}-\d{2}-\d{4}/, // SSN
    /\$\d{4,}/, // Large dollar amounts
    /USD\s*\d{4,}/, // Currency amounts
  ];

  for (const pattern of piiPatterns) {
    if (pattern.test(dataString)) {
      console.warn('[GEMINI] PII pattern detected in sanitized data:', pattern);
      return false;
    }
  }

  return true;
};

export default {
  sanitizePortfolioForGemini,
  validateNoPII
};
